import {Accordion} from '@mantine/core';
import {Box} from 'src/design/atoms/Box';
import {Divider} from 'src/design/atoms/Divider';
import {notifications} from '@mantine/notifications';
import React, {useRef, useState} from 'react';
import {TaskTypeEnum} from 'src/api/ProactApiClient';
import {useSupervisorApprovalAcknowledgeToolboxTalkMutation} from 'src/redux/queries/taskAssignment';
import {useGetTaskDefinitionDetailsQuery} from 'src/redux/queries/taskDefinition';
import {Description} from 'src/design/atoms/Description';
import {SectionTitle} from 'src/design/atoms/Typography';
import {Title} from 'src/design/atoms/Title';
import {QrReader} from 'src/design/organisms/QrReader';
import {ContentBody} from 'src/app/content-pdf/ContentBody';
import {hasContentBody} from 'src/app/content-pdf/contentPdf';
import {TableBodyRender} from 'src/design/molecules/TableBodyRender';
import {useAlert} from 'src/shared/hooks/useAlert';
import {extractPersonnelIdFromBadge} from 'src/shared/utils/extractPersonnelIdFromBadge';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {SupervisorApprovalAssignmentsList} from './SupervisorApprovalAssignmentsList';

export interface SupervisorApprovalFormProps {
  taskId: number;
}

export const SupervisorApprovalForm: React.FC<SupervisorApprovalFormProps> = ({
  taskId,
}) => {
  const {data, isLoading} = useGetTaskDefinitionDetailsQuery(taskId);
  const {setAlert} = useAlert();
  const [acknowledgeToolboxTalk] =
    useSupervisorApprovalAcknowledgeToolboxTalkMutation();
  const [counter, setCounter] = useState<number>(0);

  // Cache scanned barcodes
  const scannedCache = useRef<Set<number>>(new Set());

  // Use refs for lastProcessedTime and lastScanned
  const lastProcessedTime = useRef<number | null>(null);
  const lastScanned = useRef<string | null>(null);

  // Function to trigger alerts and maintain a rolling window of 10 alerts
  const triggerAlert = (message: string, type: 'success' | 'error') => {
    notifications.show({
      color: type === 'success' ? 'green' : 'red',
      title: type === 'success' ? 'Success!' : 'Unexpected issue',
      message: message,
    });
  };

  const processBarcode = async (barcode: string) => {
    try {
      const userId = extractPersonnelIdFromBadge(barcode);
      if (!userId) {
        triggerAlert(
          `Invalid barcode scanned. Expected personnel data but found none.`,
          'error'
        );
        return;
      }

      if (scannedCache.current.has(userId)) {
        triggerAlert(
          `Barcode has already been scanned for this task.`,
          'error'
        );
        return;
      }

      const msg = await acknowledgeToolboxTalk({taskId, userId}).unwrap();

      triggerAlert(msg, 'success');

      scannedCache.current.add(userId);
      setCounter(counter + 1);
    } catch (error) {
      triggerAlert(parseErrorMessage(error), 'error');
    }
  };

  function handleQrScanned(result: string): void {
    const now = Date.now(); // Get current time in milliseconds

    if (!result) return;

    // If it's a new barcode, process it immediately
    if (result !== lastScanned.current) {
      void processBarcode(result);
      lastScanned.current = result;
      lastProcessedTime.current = now; // Update last processed time for the new barcode
    } else {
      // If the same barcode, check for throttling (1 second)
      const timeSinceLastProcess = now - (lastProcessedTime.current ?? 0);

      if (lastProcessedTime.current === null || timeSinceLastProcess >= 5000) {
        void processBarcode(result); // Process again if 1 second has passed
        lastProcessedTime.current = now; // Update the last processed time
      }
    }
  }

  return isLoading || !data ? (
    <TableBodyRender isLoading={isLoading} />
  ) : (
    <>
      <Title fz={{base: 'h3', md: 'h2'}}>
        {`Supervisor Approval for ${data.subject}`}
      </Title>

      <Box mt="md">
        <QrReader
          onScanned={handleQrScanned}
          onError={message => setAlert(message, 'error')}
        />
      </Box>

      <Accordion variant="separated" mt="md">
        {hasContentBody(data.content, data.contentPdfUrl) && (
          <Accordion.Item value={'content'}>
            <Accordion.Control>
              <SectionTitle text={`Toolbox Topic`} />
            </Accordion.Control>
            <Accordion.Panel>
              <ContentBody
                content={data.content}
                contentPdfUrl={data.contentPdfUrl}
              />
            </Accordion.Panel>
          </Accordion.Item>
        )}
        <Accordion.Item value={'assignments'}>
          <Accordion.Control>
            <SectionTitle text={`Incomplete`} />
          </Accordion.Control>
          <Accordion.Panel>
            <SupervisorApprovalAssignmentsList
              taskType={TaskTypeEnum.ToolboxTalk}
              taskId={data.id}
              refetchId={counter}
            />
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>

      <Divider mt="md" mb="md" />
      <Description
        text={`As a supervisor, you may acknowledge ${data.subject} on behalf of other users. The employee must present their badge for scanning for each acknowledgement. Use the badge scanner below to scan their badge and have ${data.subject} acknowledged on their behalf. You may continue scanning additional badges to complete multiple approvals.`}
      />
    </>
  );
};
